import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { getProcessedProductTrace } from "../../../components/firebase/processedProductTrace";
import { formatTraceDate } from "./FarmProductTraceTimeline";
import "./farmProductTrace.css";

function Timeline({ group, language, method }) {
  const sv = language === 0;
  const steps = [
    { title: method === "dehydrated" ? (sv ? "Torkad" : "Dehydrated") : (sv ? "Frystorkad" : "Freeze-dried"), date: "processDate", person: "processedBy", Icon: SpaOutlinedIcon },
    { title: sv ? "Packad för dig" : "Packed for you", date: "packingDate", person: "packedBy", Icon: Inventory2OutlinedIcon },
    { title: sv ? "Bäst före" : "Best before", date: "bestBeforeDate", Icon: EventAvailableOutlinedIcon },
  ];
  return <article className="BatchTraceDetail" aria-labelledby="processed-trace-detail-title">
    <div className="BatchTraceDetailHeading">
      <div><p className="BatchTraceEyebrow">{sv ? "DIN BATCH" : "YOUR BATCH"}</p><h3 id="processed-trace-detail-title">{group.code}</h3></div>
      <span className="BatchTraceBadge">{sv ? "Packad" : "Packed"}</span>
    </div>
    {group.batches.length > 1 && <p className="BatchTraceLotNote">{sv
      ? "Här finns flera packningar från samma dag. Matcha även bäst före-datumet med din etikett."
      : "There are several packing records for this date. Match the best-before date to your label too."}</p>}
    {group.batches.map((batch, index) => <ol className="BatchTraceJourney BatchTraceProcessedJourney" key={index} aria-label={sv ? "Från råvara till förpackning" : "From ingredient to package"}>
      {steps.map(({ title, date, person, Icon }) => <li key={date}>
        <span className="BatchTraceStepIcon"><Icon aria-hidden="true" /></span>
        <div><h4>{title}</h4><p>{batch[date] ? <time dateTime={batch[date]}>{formatTraceDate(batch[date], language)}</time> : (sv ? "Ej registrerat" : "Not recorded")}</p>
          {person && <span className="BatchTracePerson">{batch[person] ? `${sv ? "Av" : "By"} ${batch[person]}` : (sv ? "Namn saknas i registreringen" : "Name not recorded")}</span>}
        </div>
      </li>)}
    </ol>)}
  </article>;
}

export default function FarmProcessedProductTrace({ product, language }) {
  const sv = language === 0;
  const { productId, batchDate } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const section = useRef(null);
  const scrolled = useRef(null);

  useEffect(() => {
    let active = true;
    setBatches([]); setLoading(true); setError(false);
    getProcessedProductTrace({ id: product.id, firebaseId: product.firebaseId }).then((items) => {
      if (active) { setBatches(items); setLoading(false); }
    }).catch(() => { if (active) { setError(true); setLoading(false); } });
    return () => { active = false; };
  }, [product.id, product.firebaseId, attempt]);

  const groups = [...batches.reduce((result, batch) => {
    const code = batch.batchLabel;
    if (!result.has(code)) result.set(code, { code, packingDate: batch.packingDate, batches: [] });
    const group = result.get(code);
    // Different package sizes can share the same history. Retain distinct
    // histories intact so processing/people/best-before dates stay associated.
    if (!group.batches.some((existing) => JSON.stringify(existing) === JSON.stringify(batch))) group.batches.push(batch);
    return result;
  }, new Map()).values()];
  const requested = batchDate || searchParams.get("batch") || searchParams.get("date");
  const match = groups.find((group) => group.code === requested || group.packingDate === requested);
  const selected = requested ? match : groups[0];
  const matchedCode = match?.code;

  useEffect(() => {
    if (!loading && !error && matchedCode && requested && scrolled.current !== requested) {
      scrolled.current = requested;
      section.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [loading, error, matchedCode, requested]);

  const selectBatch = (group) => {
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    scrolled.current = group.code;
    if (batchDate) {
      next.delete("batch");
      navigate({ pathname: `/farm/products/processed/${encodeURIComponent(productId)}/${group.code}`, search: next.toString() }, { replace: true, preventScrollReset: true });
    } else {
      next.set("batch", group.code);
      setSearchParams(next, { replace: true, preventScrollReset: true });
    }
  };

  return <section className="BatchTrace BatchTraceProcessed" id="ProductTrace" ref={section} aria-labelledby="processed-trace-title">
    <div className="BatchTraceHeader"><div>
      <h2 id="processed-trace-title">{sv ? "SPÅRA DIN PRODUKT" : "TRACE YOUR PRODUCT"}</h2>
      <p>{sv ? "Välj den sexsiffriga datumkoden på din etikett. Här ser du när produkten processades och packades, vem som tog hand om den och vilket bäst före-datum som registrerats."
        : "Choose the six-digit date code on your label to see when your product was processed and packed, who handled it and its recorded best-before date."}</p>
    </div></div>
    {loading ? <div className="BatchTraceMessage" role="status"><span className="BatchTracePulse" />{sv ? "Hämtar din produkts historia…" : "Loading your product’s history…"}</div>
      : error ? <div className="BatchTraceMessage" role="alert"><p>{sv ? "Vi kunde inte hämta spårningen just nu. Försök gärna igen." : "We couldn’t load the tracing details right now. Please try again."}</p><button type="button" onClick={() => setAttempt((value) => value + 1)}>{sv ? "Försök igen" : "Try again"}</button></div>
      : !groups.length ? <div className="BatchTraceMessage" role="status"><SpaOutlinedIcon /><h3>{sv ? "Ingen batch att visa ännu" : "No batches to show yet"}</h3><p>{sv ? "Här visas spårningen när en packning har registrerats för produkten." : "Tracing details appear here once a packing record is available for this product."}</p></div>
      : <>
        <div className="BatchTracePickerIntro"><div className="BatchTracePickerHeading"><h3>{sv ? "Välj din batch" : "Choose your batch"}</h3></div><p>{sv ? "Datumkoden är packningsdatumet, skrivet som ÅÅMMDD." : "The date code is the packing date, written as YYMMDD."}</p></div>
        <div className="BatchTraceWorkspace"><div className="BatchTracePicker"><div className="BatchTraceChoices" role="group" aria-label={sv ? "Packningsdatum" : "Packing dates"}>
          {groups.map((group) => <button type="button" key={group.code} aria-pressed={selected?.code === group.code} onClick={() => selectBatch(group)}><span><strong>{group.code}</strong><time dateTime={group.packingDate}>{formatTraceDate(group.packingDate, language)}</time></span><ArrowForwardIcon aria-hidden="true" /></button>)}
        </div></div>
          {selected ? <Timeline group={selected} language={language} method={product.method} /> : <div className="BatchTraceMessage" role="status"><h3>{sv ? "Batchen är inte tillgänglig" : "Batch not available"}</h3><p>{sv ? "Vi hittar inte datumkoden. Kontrollera etiketten och välj ett packningsdatum i listan." : "We couldn’t find that date code. Check your label and choose a packing date from the list."}</p></div>}
        </div>
      </>}
  </section>;
}
